/**
 * Formats a bare ISO date string (`YYYY-MM-DD`, e.g. epayroll's
 * `mostRecentSeparation.effectiveDate`) in the active locale.
 *
 * A local copy rather than a shared import: spec 008 introduced the same
 * utility in mfe-pot-msca-shell, but repos in this family don't import each
 * other's source -- only published `@accenturecodefoundry/*` packages cross
 * the boundary, and this is too small to warrant one.
 *
 * Appending a local midnight time avoids `new Date(...)` parsing a bare date
 * as UTC midnight, which would roll the displayed day back by one in any
 * timezone behind UTC.
 *
 * Returns undefined, rather than throwing or formatting garbage, for
 * anything that isn't a bare `YYYY-MM-DD` date -- callers treat undefined as
 * "show nothing for this row," never as an error.
 */
export function formatIsoDate(isoDate: string, locale: 'en' | 'fr'): string | undefined {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(isoDate)) {
    return undefined;
  }
  const date = new Date(`${isoDate}T00:00:00`);
  if (Number.isNaN(date.getTime())) {
    return undefined;
  }
  return new Intl.DateTimeFormat(locale === 'fr' ? 'fr-CA' : 'en-CA', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(date);
}

const MS_PER_DAY = 24 * 60 * 60 * 1000;

function intlLocale(locale: 'en' | 'fr'): string {
  return locale === 'fr' ? 'fr-CA' : 'en-CA';
}

/**
 * A two-week reporting period (docs/specs/016-ei-reporting-flow.md) as a
 * short locale-aware range, e.g. "Oct 1 – 14" / "1 – 14 oct.".
 *
 * Periods are stored as [start, end) ISO timestamps and are due at `end`,
 * so the range shows the *inclusive* last day (end minus one day) --
 * otherwise adjacent periods would read as overlapping ("Oct 1 – 15",
 * "Oct 15 – 29"). Formatted in UTC: the schedule is anchored on a UTC
 * `appliedAt`, and a local time zone would shift the displayed days.
 */
export function formatPeriodRange(startIso: string, endIsoExclusive: string, locale: 'en' | 'fr'): string {
  const start = new Date(startIso);
  const lastDay = new Date(new Date(endIsoExclusive).getTime() - MS_PER_DAY);
  return new Intl.DateTimeFormat(intlLocale(locale), {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  }).formatRange(start, lastDay);
}

/** A short locale-aware date in UTC, e.g. a period's due date ("Oct 15" / "15 oct."). */
export function formatShortDate(iso: string, locale: 'en' | 'fr'): string {
  return new Intl.DateTimeFormat(intlLocale(locale), { month: 'short', day: 'numeric', timeZone: 'UTC' }).format(
    new Date(iso),
  );
}

/** A dollar amount in the active locale, e.g. "$1,276.00" / "1 276,00 $". */
export function formatCurrency(amount: number, locale: 'en' | 'fr'): string {
  return new Intl.NumberFormat(intlLocale(locale), { style: 'currency', currency: 'CAD' }).format(amount);
}
